<template>
  <div class="pdf-viewport-container container" v-if="hasFile">
    <PdfViewportTools
      v-if="pages && loaded"
      :pages="pages"
      v-model="page"
    />
    <div
      ref="pdfViewport"
      class="pdf-viewport"
      @click="onViewportClick"
    >
      <component
        :is="signature.type || signatureType"
        v-for="signature in signaturesByPage"
        :key="signature.id"
        :name="signature.name"
        :x="signature.x"
        :y="signature.y"
        :transform="signature.transform"
        :bounds="bounds"
        :closable="closableSign"
        :meta="signature.meta"
        @drag-end="onSignatureDragEnd($event, signature)"
        @close="$emit('signature-delete', signature)"
      />
      <Pdf
        :class="{'hide-document': hideDocument}"
        class="m-auto"
        :page="actualPage"
        :src="file"
        ref="page"
        @page-loaded="loadedPage"
        v-if="!publicandoFile"
      />
    </div>
    <b-overlay :show="!loaded" no-wrap />
  </div>
</template>
<script>

import Pdf from 'vue-pdf-signature';

import PdfViewportTools from '@/febos/global/pdf/components/PdfViewportTools';
import DraggableSignature from '@/febos/global/pdf/components/DraggableSignature';
import { ioDownloadPrivateFile } from '@/febos/servicios/api/herramientas.api';
import { SIGNATURE_TYPES } from '@/febos/global/pdf/config';

export default {
  components: {
    Pdf,
    PdfViewportTools,
    DraggableSignature
  },
  data() {
    return {
      pages: undefined,
      file: undefined,
      page: this.defaultPage,
      bounds: undefined,
      loaded: false,
      publicandoFile: false,
      pdfDocument: undefined,
      signatureAnnotationsCache: {}
    };
  },
  watch: {
    s3url() {
      this.loadFile();
    },
    path() {
      this.loadFile();
    }
  },
  computed: {
    actualPage() {
      if (this.page === -1) {
        return this.pages;
      }
      return this.page;
    },
    hasFile() {
      return this.s3url || this.path;
    },
    isLastPage() {
      return this.page === this.pages;
    },
    signaturesByPage() {
      return this.signatures.filter((signature) => (
        signature.page === this.page
        || (signature.page === -1 && this.isLastPage)
        || (this.page === -1 && this.pages === 1
        )
      ));
    }
  },
  methods: {
    loadedPage() {
      this.setBounds();

      if (this.loaded) {
        return;
      }

      this.$emit('loaded');
      this.loaded = true;
    },
    async getS3Url() {
      if (!this.s3url) {
        if (this.path.startsWith('http')) {
          return this.path;
        }
        this.publicandoFile = true;
        const {
          data: { url: s3FileUrl }
        } = await ioDownloadPrivateFile({
          path: this.path,
          nombre: this.name,
          descargable: false
        });
        this.publicandoFile = false;
        return s3FileUrl;
      }
      return this.s3url;
    },
    async loadFile() {
      this.clear();
      const s3FileUrl = await this.getS3Url();
      this.file = Pdf.createLoadingTask(s3FileUrl);
      const pdfLoaded = await this.file.promise;
      this.pdfDocument = pdfLoaded;
      this.pages = pdfLoaded.numPages;
    },
    clear() {
      this.file = undefined;
      this.loaded = false;
      this.bounds = undefined;
      this.pages = undefined;
      this.page = this.defaultPage;
      this.pdfDocument = undefined;
      this.signatureAnnotationsCache = {};
    },
    setXIntoViewport(cursorX, viewportX, viewportWidth) {
      if (cursorX - (this.signatureWidth / 2) < viewportX) {
        return 0;
      }

      if (cursorX + (this.signatureWidth / 2) > viewportX + viewportWidth) {
        return viewportWidth - this.signatureWidth;
      }

      return cursorX - viewportX - (this.signatureWidth / 2);
    },
    setYIntoViewport(cursorY, viewportY, viewportHeight) {
      if (cursorY - (this.signatureHeight / 2) < viewportY) {
        return 0;
      }

      if (cursorY + (this.signatureHeight / 2) > viewportY + viewportHeight) {
        return viewportHeight - this.signatureHeight;
      }

      return cursorY - viewportY - (this.signatureHeight / 2);
    },
    onViewportClick(
      {
        x: cursorWindowX,
        y: cursorWindowY
      }
    ) {
      const {
        x: pdfViewportX,
        y: pdfViewportY
      } = this.$refs.pdfViewport.getBoundingClientRect();

      const cursorX = cursorWindowX + window.scrollX + this.$refs.pdfViewport.scrollLeft;
      const cursorY = cursorWindowY + window.scrollY + this.$refs.pdfViewport.scrollTop;

      const viewportX = window.scrollX + pdfViewportX;
      const viewportY = window.scrollY + pdfViewportY;

      const viewportWidth = this.$refs.pdfViewport.scrollWidth;
      const viewportHeight = this.$refs.pdfViewport.scrollHeight;

      this.$emit(
        'page-click',
        {
          x: this.setXIntoViewport(cursorX, viewportX, viewportWidth),
          y: this.setYIntoViewport(cursorY, viewportY, viewportHeight)
        },
        this.page
      );
    },
    onSignatureDragEnd(
      {
        x: signatureX,
        y: signatureY,
        transform
      },
      signature
    ) {
      const {
        x: pdfViewportX,
        y: pdfViewportY,
      } = this.$refs.pdfViewport.getBoundingClientRect();

      const viewportX = pdfViewportX - this.$refs.pdfViewport.scrollLeft;
      const viewportY = pdfViewportY - this.$refs.pdfViewport.scrollTop;

      this.$emit(
        'signature-drag-end',
        {
          x: Math.max(signatureX - viewportX, 0),
          y: Math.max(signatureY - viewportY, 0),
          transform
        },
        signature
      );
    },
    setBounds() {
      const {
        left: pdfViewportLeft,
        top: pdfViewportTop,
        width,
        height
      } = this.$refs.pdfViewport.getBoundingClientRect();

      const top = window.scrollY + pdfViewportTop;
      const left = window.scrollX + pdfViewportLeft;

      this.bounds = {
        left,
        top,
        right: left + width,
        bottom: top + height
      };
    },
    getPageWidth() {
      return this.$refs.pdfViewport.scrollWidth;
    },
    getPageHeight() {
      return this.$refs.pdfViewport.scrollHeight;
    },
    pdfRef() {
      return this.$refs.pdfViewport;
    },
    getPageNumber() {
      return this.page;
    },
    async getSignatureAnnotations(pageNumber = this.page) {
      if (!this.pdfDocument) {
        return [];
      }

      const safePage = Math.min(
        Math.max(pageNumber || 1, 1),
        this.pages || 1
      );

      if (this.signatureAnnotationsCache[safePage]) {
        return this.signatureAnnotationsCache[safePage];
      }

      try {
        const page = await this.pdfDocument.getPage(safePage);
        const annotations = await page.getAnnotations({ intent: 'display' });
        const viewport = page.getViewport({ scale: 1 });

        const isSignatureAnnotation = (annotation) => {
          if (!annotation) {
            return false;
          }
          if (annotation.fieldType === 'Sig') {
            return true;
          }
          const alternative = (annotation.alternativeText || '').toLowerCase();
          const fieldName = (annotation.fieldName || '').toLowerCase();
          return alternative.includes('firma') || fieldName.includes('firma');
        };

        const signatures = annotations
          .filter((annotation) => (
            isSignatureAnnotation(annotation)
            && Array.isArray(annotation.rect)
          ))
          .map((annotation) => {
            const [x1, y1, x2, y2] = viewport.convertToViewportRectangle(annotation.rect);
            const left = Math.min(x1, x2);
            const top = Math.min(y1, y2);
            const width = Math.abs(x1 - x2);
            const height = Math.abs(y1 - y2);
            return {
              id: annotation.id,
              x: left,
              y: top,
              width,
              height
            };
          });

        this.$set(this.signatureAnnotationsCache, safePage, signatures);
        return signatures;
      } catch (error) {
        console.error('Error obtaining PDF signatures', error);
        return [];
      }
    }
  },
  created() {
    this.loadFile();
  },
  props: {
    defaultPage: { type: Number, default: 1 },
    s3url: { type: String },
    path: { type: String },
    name: { type: String, required: true },
    hideDocument: { type: Boolean, default: false },
    closableSign: { type: Boolean, default: false },
    signatures: { type: Array, default: () => [] },
    signatureWidth: { type: Number, default: 0 },
    signatureHeight: { type: Number, default: 0 },
    signatureType: { type: Object, default: () => SIGNATURE_TYPES.DraggableUserSignature },
  }
};

</script>

<style lang="scss" scoped>

@import '~@core/scss/base/bootstrap-extended/_include';

.pdf-viewport-container {
  flex: 1;
  background-color: $gray-100;
  padding: map-get($spacers, 1);
  overflow: auto;
}

.pdf-viewport-tools {
  display: flex;
  justify-content: flex-end;
  padding-bottom: map-get($spacers, 1);
}

.pdf-viewport {
  flex: 1;
  max-height: 85vh;
  overflow-y: auto;
  height: auto;
  margin: 0 auto;
  max-width: fit-content;
  position: relative;
}
.hide-document {
  -webkit-filter: blur(5px);
  -moz-filter: blur(5px);
  -o-filter: blur(5px);
  -ms-filter: blur(5px);
  filter: blur(5px);
  background-color: #ccc;
}

</style>
